import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getPhotometricSymbol } from '../../utils/photometricSymbols';
import {
  type PhotometricTarget,
  TARGET_OPTIONS,
  type TargetOption,
  validatePhotometricTargets,
} from '../../utils/photometricTargets';
import { getPhotometricUnit } from '../../utils/unitSystem';

/**
 * The five quantities the calculation can aim at. Uniformity is not one of them: it is a
 * ratio on a single average or exact value, so it is attached to that one row.
 */
const QUANTITIES = ['E', 'L', 'Ehs', 'Ev', 'Esc'] as const;
type Quantity = (typeof QUANTITIES)[number];
type LevelType = 'AVERAGE' | 'EXACT';

const MODE_LABEL = { AVERAGE: 'average', EXACT: 'exact', MIN: 'minimum' } as const;

const GROUP: Record<Quantity, string> = {
  E: 'horizontal',
  L: 'luminance',
  Ehs: 'hemispherical',
  Ev: 'vertical',
  Esc: 'semicylindrical',
};

interface QuantityRow {
  fn: Quantity;
  /** Average or exact level. Null means the row is a minimum only. */
  level: LevelType | null;
  levelValue: string;
  minimum: boolean;
  minValue: string;
  uniformity: boolean;
  uniformityValue: string;
}

const optionFor = (fn: Quantity, type: 'AVERAGE' | 'EXACT' | 'MIN'): TargetOption => {
  const found = TARGET_OPTIONS.find((option) => option.function === fn && option.type === type);
  if (!found) throw new Error(`No ${type} option for ${fn}`);
  return found;
};

const supportsExact = (fn: Quantity) =>
  TARGET_OPTIONS.some((option) => option.function === fn && option.type === 'EXACT');

const rowsFromTargets = (targets: readonly PhotometricTarget[], allowed: readonly Quantity[]): QuantityRow[] => {
  const uniformity = targets.find((target) => target.function === 'Uo');
  const levelFunctions = targets.filter((target) => target.type === 'AVERAGE' || target.type === 'EXACT');
  return allowed.flatMap((fn) => {
    const level = targets.find(
      (target) => target.function === fn && (target.type === 'AVERAGE' || target.type === 'EXACT'),
    );
    const minimum = targets.find((target) => target.function === fn && target.type === 'MIN');
    if (!level && !minimum) return [];
    const ownsUniformity = !!uniformity && level?.type === 'AVERAGE' && levelFunctions.length === 1;
    return [
      {
        fn,
        level: level ? (level.type as LevelType) : null,
        levelValue: String(level?.value ?? optionFor(fn, 'AVERAGE').defaultValue),
        minimum: !!minimum && level?.type !== 'EXACT',
        minValue: String(minimum?.value ?? optionFor(fn, 'MIN').defaultValue),
        uniformity: ownsUniformity,
        uniformityValue: String(uniformity?.value ?? 0.4),
      },
    ];
  });
};

/** Drop uniformity unless it refers to exactly one average. An exact value cannot carry it. */
const normalized = (rows: readonly QuantityRow[]): QuantityRow[] => {
  const levelCount = rows.filter((row) => row.level).length;
  let kept = false;
  return rows.map((row) => {
    const keep = row.uniformity && row.level === 'AVERAGE' && levelCount === 1 && !kept;
    if (keep) kept = true;
    return keep ? row : { ...row, uniformity: false };
  });
};

const rowsToTargets = (rows: readonly QuantityRow[]): PhotometricTarget[] | null => {
  const targets: PhotometricTarget[] = [];
  for (const row of normalized(rows)) {
    if (!row.level && !row.minimum) return null;
    if (row.level) {
      const value = Number(row.levelValue);
      if (!(value > 0)) return null;
      targets.push({ function: row.fn, type: row.level, value });
    }
    if (row.minimum || !row.level) {
      const value = Number(row.minValue);
      if (!(value > 0)) return null;
      targets.push({ function: row.fn, type: 'MIN', value });
    }
    if (row.uniformity) {
      const value = Number(row.uniformityValue);
      if (!(value > 0) || value > 1) return null;
      targets.push({ function: 'Uo', type: 'MIN', value });
    }
  }
  return targets;
};

/** Exact fixes the level, so it cannot keep a separate minimum or a uniformity. */
const withMode = (row: QuantityRow, next: LevelType | 'MIN'): QuantityRow => {
  if (next === 'MIN') {
    return {
      ...row,
      level: null,
      minimum: true,
      minValue: row.level ? row.levelValue : row.minValue,
      uniformity: false,
    };
  }
  return {
    ...row,
    level: next,
    minimum: next === 'EXACT' ? false : row.level ? row.minimum : false,
    uniformity: next === 'EXACT' ? false : row.uniformity,
  };
};

const sameTargets = (a: readonly PhotometricTarget[], b: readonly PhotometricTarget[]) => {
  const key = (target: PhotometricTarget) => `${target.function}:${target.type}:${target.value}`;
  const left = a.map(key).sort();
  const right = b.map(key).sort();
  return left.length === right.length && left.every((item, index) => item === right[index]);
};

interface CustomTargetsFormProps {
  /** Currently saved targets. An empty list means the layer is not using custom targets. */
  value: readonly PhotometricTarget[];
  onApply: (targets: PhotometricTarget[]) => void;
  /** Quantities this surface can calculate. A façade only supports horizontal illuminance. */
  quantities?: readonly Quantity[];
}

/**
 * Compact editor for an individual requirement. "+" adds one of the five calculated
 * quantities. Uniformity can be switched on for the single average only.
 */
const applicableTargets = (
  targets: readonly PhotometricTarget[],
  allowed: readonly Quantity[],
): PhotometricTarget[] => {
  const allowedSet = new Set<string>(allowed);
  const photometric = targets.filter((target) => target.function !== 'Uo' && allowedSet.has(target.function));
  const levels = photometric.filter((target) => target.type === 'AVERAGE' || target.type === 'EXACT');
  const uniformity = targets.find((target) => target.function === 'Uo');
  if (uniformity && levels.length === 1 && levels[0].type === 'AVERAGE') return [...photometric, uniformity];
  return photometric;
};

export function CustomTargetsForm({ value, onApply, quantities = QUANTITIES }: CustomTargetsFormProps) {
  const { t } = useTranslation();
  const savedKey = JSON.stringify(value);
  const quantitiesKey = quantities.join(',');
  const valueRef = useRef(value);
  valueRef.current = value;
  const onApplyRef = useRef(onApply);
  onApplyRef.current = onApply;
  const [rows, setRows] = useState<QuantityRow[]>(() =>
    rowsFromTargets(applicableTargets(value, quantities), quantities),
  );
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const replaceRows = (next: QuantityRow[]) => {
    rowsRef.current = next;
    setRows(next);
    return next;
  };

  useEffect(() => {
    const allowed = quantitiesKey.split(',') as Quantity[];
    const next = applicableTargets(valueRef.current, allowed);
    if (!sameTargets(next, valueRef.current)) {
      onApplyRef.current(next);
      return;
    }
    setRows(rowsFromTargets(next, allowed));
  }, [savedKey, quantitiesKey]);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  const issue = (() => {
    const targets = rowsToTargets(rows);
    if (!targets) return { code: 'bad-value' as const };
    if (targets.length === 0) return null;
    return validatePhotometricTargets(targets);
  })();

  const commit = (next: QuantityRow[]) => {
    const rowsNext = replaceRows(normalized(next));
    const targets = rowsToTargets(rowsNext);
    if (!targets) {
      const allowed = quantitiesKey.split(',') as Quantity[];
      replaceRows(rowsFromTargets(applicableTargets(valueRef.current, allowed), allowed));
      return;
    }
    if (targets.length > 0 && validatePhotometricTargets(targets)) return;
    if (sameTargets(targets, valueRef.current)) return;
    onApply(targets);
  };

  const add = (fn: Quantity) => {
    const levelTaken = rows.some((row) => row.level);
    const uniformityOn = rows.some((row) => row.uniformity);
    const asMinimum = uniformityOn && levelTaken;
    setMenuOpen(false);
    commit([
      ...rows,
      {
        fn,
        level: asMinimum ? null : 'AVERAGE',
        levelValue: String(optionFor(fn, 'AVERAGE').defaultValue),
        minimum: asMinimum,
        minValue: String(optionFor(fn, 'MIN').defaultValue),
        uniformity: false,
        uniformityValue: '0.4',
      },
    ]);
  };

  const patch = (fn: Quantity, change: (row: QuantityRow) => QuantityRow) => {
    commit(rows.map((row) => (row.fn === fn ? change(row) : row)));
  };

  const available = quantities.filter((fn) => !rows.some((row) => row.fn === fn));
  const levelCount = rows.filter((row) => row.level).length;

  return (
    <div className="space-y-2" onClick={(event) => event.stopPropagation()}>
      {rows.map((row) => {
        const unit = getPhotometricUnit(row.fn) ?? '';
        const name = t(`workspace.customTargets.groups.${GROUP[row.fn]}`);
        const shownValue = row.level ? row.levelValue : row.minValue;
        const spec = row.level ? optionFor(row.fn, row.level) : optionFor(row.fn, 'MIN');
        const uniformityOwner = rows.some((item) => item.uniformity && item.fn !== row.fn);
        const canBeLevel = !uniformityOwner;
        const showUniformity = row.level === 'AVERAGE' && levelCount === 1;
        const modes: Array<LevelType | 'MIN'> = [
          ...(canBeLevel ? (['AVERAGE'] as const) : []),
          ...(canBeLevel && supportsExact(row.fn) && !row.uniformity ? (['EXACT'] as const) : []),
          'MIN',
        ];
        return (
          <div
            key={row.fn}
            className={`rounded-xl border bg-[#1a1a1a] p-2.5 ${
              row.uniformity ? 'border-lime-400/70 shadow-[0_0_0_1px_rgba(179,236,73,0.25)]' : 'border-white/10'
            }`}
          >
            <div className="flex items-center gap-2">
              <SymbolBadge fn={row.fn} type={row.level ?? 'MIN'} active={row.uniformity} />
              <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-white">{name}</span>
              <button
                type="button"
                aria-label={t('workspace.customTargets.remove')}
                onClick={() => commit(rows.filter((item) => item.fn !== row.fn))}
                className="flex h-6 w-6 items-center justify-center rounded-full text-gray-500 hover:bg-white/10 hover:text-white"
              >
                ×
              </button>
            </div>

            <div className="mt-2 flex items-center gap-2">
              <div className="inline-flex rounded-full bg-black p-0.5" role="group" aria-label={name}>
                {modes.map((mode) => {
                  const selected = (row.level ?? 'MIN') === mode;
                  return (
                    <button
                      key={mode}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => patch(row.fn, (current) => withMode(current, mode))}
                      className={`h-6 rounded-full px-2 text-[11px] font-medium transition ${
                        selected ? 'bg-lime-400 text-black' : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      {t(`workspace.customTargets.${MODE_LABEL[mode]}`)}
                    </button>
                  );
                })}
              </div>
              <ValueField
                label={name}
                unit={unit}
                spec={spec}
                value={shownValue}
                onChange={(next) =>
                  replaceRows(
                    rowsRef.current.map((item) =>
                      item.fn !== row.fn
                        ? item
                        : row.level
                          ? { ...item, levelValue: next }
                          : { ...item, minValue: next },
                    ),
                  )
                }
                onBlur={() => commit(rowsRef.current)}
              />
              {showUniformity && (
                <div className="ml-auto flex items-center gap-1">
                  {row.uniformity && (
                    <ValueField
                      label={t('workspace.customTargets.groups.uniformity')}
                      unit=""
                      spec={{ min: 0.05, max: 1, step: 0.05 }}
                      value={row.uniformityValue}
                      onChange={(next) =>
                        replaceRows(
                          rowsRef.current.map((item) =>
                            item.fn === row.fn ? { ...item, uniformityValue: next } : item,
                          ),
                        )
                      }
                      onBlur={() => commit(rowsRef.current)}
                    />
                  )}
                  <button
                    type="button"
                    aria-pressed={row.uniformity}
                    aria-label={t('workspace.customTargets.groups.uniformity')}
                    onClick={() => patch(row.fn, (current) => ({ ...current, uniformity: !current.uniformity }))}
                    className={`h-6 rounded-full px-2 text-[11px] font-semibold transition ${
                      row.uniformity
                        ? 'bg-lime-400 text-black'
                        : 'border border-white/15 text-gray-300 hover:border-lime-400 hover:text-lime-300'
                    }`}
                  >
                    U₀
                  </button>
                </div>
              )}
            </div>

            {row.level === 'AVERAGE' && (
              <div className="mt-2 flex items-center gap-2">
                {row.minimum ? (
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] uppercase tracking-wide text-gray-500">
                      {t('workspace.customTargets.minimum')}
                    </span>
                    <ValueField
                      label={`${name} ${t('workspace.customTargets.minimum')}`}
                      unit={unit}
                      spec={optionFor(row.fn, 'MIN')}
                      value={row.minValue}
                      onChange={(next) =>
                        replaceRows(
                          rowsRef.current.map((item) => (item.fn === row.fn ? { ...item, minValue: next } : item)),
                        )
                      }
                      onBlur={() => commit(rowsRef.current)}
                    />
                    <button
                      type="button"
                      aria-label={t('workspace.customTargets.remove')}
                      onClick={() => patch(row.fn, (current) => ({ ...current, minimum: false }))}
                      className="text-[13px] text-gray-500 hover:text-white"
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => patch(row.fn, (current) => ({ ...current, minimum: true }))}
                    className="h-6 rounded-full border border-dashed border-white/20 px-2 text-[11px] text-gray-300 hover:border-lime-400 hover:text-lime-300"
                  >
                    + {t('workspace.customTargets.minimum')}
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}

      {issue && (
        <p className="text-[11px] leading-snug text-amber-300">{t(`workspace.customTargets.issues.${issue.code}`)}</p>
      )}

      {available.length > 0 && (
        <div ref={menuRef}>
          <button
            type="button"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
            className="flex h-9 w-full items-center justify-center gap-2 rounded-xl border border-dashed border-lime-400/60 bg-lime-400/10 text-[12px] font-medium text-lime-300 transition hover:border-lime-400 hover:bg-lime-400/20"
          >
            <span className="text-[16px] leading-none">+</span>
            {t('workspace.customTargets.add')}
          </button>
          {menuOpen && (
            <div className="mt-1.5 grid grid-cols-2 gap-1.5">
              {available.map((fn) => (
                <button
                  key={fn}
                  type="button"
                  onClick={() => add(fn)}
                  className="flex items-center gap-2 rounded-xl border border-white/10 bg-[#1a1a1a] px-2 py-2 text-left transition hover:border-lime-400 hover:bg-lime-400/10"
                >
                  <SymbolBadge fn={fn} type="EXACT" />
                  <span className="text-[11px] leading-tight text-gray-100">
                    {t(`workspace.customTargets.groups.${GROUP[fn]}`)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SymbolBadge({ fn, type, active }: { fn: Quantity; type: 'AVERAGE' | 'EXACT' | 'MIN'; active?: boolean }) {
  return (
    <span
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[12px] font-semibold ${
        active ? 'bg-lime-400 text-black' : 'bg-lime-400/15 text-lime-300'
      }`}
    >
      {getPhotometricSymbol(fn, type)}
    </span>
  );
}

function ValueField({
  label,
  unit,
  spec,
  value,
  onChange,
  onBlur,
}: {
  label: string;
  unit: string;
  spec: { min: number; max: number; step: number };
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
}) {
  return (
    <label className="flex h-7 items-center rounded-full bg-black px-2">
      <input
        type="number"
        inputMode="decimal"
        aria-label={label}
        min={spec.min}
        max={spec.max}
        step={spec.step}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
        className="w-12 bg-transparent text-right text-[13px] font-medium text-white outline-none"
      />
      {unit && <span className="ml-1 text-[10px] text-gray-500">{unit}</span>}
    </label>
  );
}
